import {
  apiClient,
  circuitBreaker,
  integrationCache,
  rateLimiter,
  type ApiClient,
  type CircuitBreaker,
  type IntegrationCache,
  type RateLimiter,
} from '../api'
import { MockSupplierAdapter } from '../adapters/MockSupplierAdapter'
import type { ISupplierAdapter } from '../adapters/ISupplierAdapter'
import { integrationConfigRepository } from '../repositories'

const DEFAULT_SUPPLIER_CODES = [
  'pauta',
  'oderco',
  'agis',
  'allnations',
  'ingram',
  'snd',
  'kabum',
  'terabyte',
  'pichau',
  'amazon',
  'mercadolivre',
  'magazineluiza',
] as const

/**
 * Connector registry — maps supplier codes to ISupplierAdapter implementations.
 */
export class IntegrationConnectorRegistry {
  private readonly adapters = new Map<string, ISupplierAdapter>()
  private readonly client: ApiClient
  private readonly cache: IntegrationCache
  private readonly limiter: RateLimiter
  private readonly breaker: CircuitBreaker

  constructor(
    client: ApiClient = apiClient,
    cache: IntegrationCache = integrationCache,
    limiter: RateLimiter = rateLimiter,
    breaker: CircuitBreaker = circuitBreaker,
  ) {
    this.client = client
    this.cache = cache
    this.limiter = limiter
    this.breaker = breaker
  }

  ensureDefaults(codes: readonly string[] = DEFAULT_SUPPLIER_CODES): void {
    const config = integrationConfigRepository.get()
    for (const code of codes) {
      if (this.adapters.has(code)) continue
      this.register(
        new MockSupplierAdapter(code, {
          client: this.client,
          cache: this.cache,
          rateLimiter: this.limiter,
          circuitBreaker: this.breaker,
          cacheTtlMs: config.cacheTtlMs,
        }),
      )
    }
  }

  register(adapter: ISupplierAdapter): void {
    this.adapters.set(adapter.supplierCode, adapter)
  }

  get(supplierCode: string): ISupplierAdapter | null {
    this.ensureDefaults()
    return this.adapters.get(supplierCode) ?? null
  }

  list(): ISupplierAdapter[] {
    this.ensureDefaults()
    return [...this.adapters.values()]
  }

  codes(): string[] {
    return this.list().map((a) => a.supplierCode)
  }
}

export const integrationConnectorRegistry = new IntegrationConnectorRegistry()

// Configure mock HTTP transport once
apiClient.setMockHandler(async (request) => {
  // Simulate occasional latency; never hits real network
  await new Promise((r) => setTimeout(r, 5 + Math.random() * 25))
  if (request.url.includes('/fail')) {
    return { status: 500, data: { error: 'mock failure' } }
  }
  return {
    status: 200,
    data: {
      ok: true,
      items: [],
    },
  }
})
